<template>
  <div :class="$style.action">
    <div :class="$style.heading_box">
      <h4>お買い物で貯める</h4>
      <p
        >公式オンラインストアと直営店舗（TENTIAL Official
        Store）共通で、会員ステージとお買い物金額（税込）に応じマイルを獲得できます。</p
      >
    </div>
    <PerfectScrollbar :class="$style.table_container">
      <table :class="$style.stage_table">
        <thead>
          <tr>
            <th>会員ステージ</th>
            <td
              v-for="stage of stageList"
              :key="stage.name"
              :class="[
                {
                  [$style.regular]: stage.name === 'REGULAR',
                  [$style.silver]: stage.name === 'SILVER',
                  [$style.gold]: stage.name === 'GOLD',
                  [$style.platinum]: stage.name === 'PLATINUM',
                },
              ]"
            >
              <h6>{{ stage.name }}</h6>
              <p>{{ stage.kana }}</p>
              <p v-if="stage.mile"
                >年間<span :class="$style.mile">{{ stage.mile.toLocaleString() }}</span
                >マイル獲得</p
              >
              <p v-else>{{ stage.description }}</p>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>購入時の<br />マイル還元率</th>
            <td
              v-for="rate of rateList"
              :key="rate"
              :class="[
                {
                  [$style.regular]: rate === 3,
                  [$style.silver]: rate === 4,
                  [$style.gold]: rate === 5,
                  [$style.platinum]: rate === 7,
                },
              ]"
              >{{ rate }}%</td
            >
          </tr>
        </tbody>
      </table>
    </PerfectScrollbar>
    <h5>また以下の条件を満たすお買い物の場合はさらにマイルを獲得できます。</h5>
    <PerfectScrollbar :class="$style.table_container">
      <table :class="$style.event_table">
        <thead>
          <tr>
            <th>イベント</th>
            <th>付与回数</th>
            <th>付与マイル</th>
            <th>条件</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="event of eventList" :key="event.name">
            <td :class="$style.event_cell">{{ event.name }}</td>
            <td>{{ event.times }}</td>
            <td>{{ `${event.mile.toLocaleString()}マイル` }}</td>
            <td
              ><span
                >{{ event.condition }}
                <template v-if="event.linkUrl && event.linkText">
                  <br />
                  <AtomsConsumerLink :url="event.linkUrl">{{ event.linkText ?? "詳細を確認する" }}</AtomsConsumerLink>
                </template>
              </span></td
            >
          </tr>
        </tbody>
      </table>
    </PerfectScrollbar>
    <div :class="$style.note_box">
      <p v-for="note of firstNoteList" :key="note">{{ note }}</p>
    </div>
    <div :class="$style.heading_box">
      <h4>アクションで貯める</h4>
      <p>商品のお気に入り登録やメールマガジンの受信設定など様々なアクションを行うことでもマイルを獲得できます。</p>
    </div>
    <div :class="$style.action_box">
      <GuideActionList :card-list="cardList" />
    </div>
    <div :class="$style.note_box">
      <p v-for="note of secondNoteList" :key="note">{{ note }}</p>
    </div>
    <AtomsConsumerButton block variant="secondary" @click="() => router.push('/mypage')">
      保有マイルを確認する</AtomsConsumerButton
    >
  </div>
</template>

<script lang="ts" setup>
import { PerfectScrollbar } from "vue3-perfect-scrollbar"
import { FAQ_URL_CROSS_PURCH_MILE } from "@tential/ec-gql-schema/const/karakuri"
import type { ApiSiteFindOneResponse } from "~/types/server/api/site"

const props = defineProps<{ mileageSet: ApiSiteFindOneResponse["mileage_set"] }>()

type StageInfo = {
  name: string
  kana: string
  description?: string
  mile?: number
}
const router = useRouter()
const stageList: StageInfo[] = [
  {
    name: "REGULAR",
    kana: "レギュラー",
    description: "会員登録",
  },
  {
    name: "SILVER",
    kana: "シルバー",
    mile: 1500,
  },
  {
    name: "GOLD",
    kana: "ゴールド",
    mile: 3000,
  },
  {
    name: "PLATINUM",
    kana: "プラチナ",
    mile: 6000,
  },
]
const rateList = [3, 4, 5, 7]

type EventInfo = {
  name: string
  times: string
  mile: number
  condition: string
  linkText?: string
  linkUrl?: string
}

const eventList: EventInfo[] = [
  {
    name: "有料でのギフト購入",
    times: "都度",
    mile: 50,
    condition: "有料ギフトラッピング資材の選択、またはeギフトでの購入1回につき付与されます",
  },
  {
    name: "複数カテゴリ商品の購入",
    times: "年間 1 回",
    mile: 500,
    condition: "Day ConditioningカテゴリとNight Conditioningカテゴリの各対象商品を購入で付与されます",
    linkText: "Day Conditioning Night Conditioningの対象商品を確認する",
    linkUrl: FAQ_URL_CROSS_PURCH_MILE,
  },
]

type Card = {
  title: string
  description: string
  icon: string
  mile?: string
  isStatus?: boolean
}

const cardList: Card[] = [
  {
    title: "メルマガ受信設定",
    mile: `${props.mileageSet.recv_mail?.mile || "300"}`,
    description: "マイル※1",
    icon: "/images/mypage/mile/mail.svg",
  },
  {
    title: "LINE ID連携",
    mile: `${props.mileageSet.line_link?.mile || "300"}`,
    description: "マイル※1",
    icon: "/images/mypage/mile/line.svg",
  },
  {
    title: "プロフィール設定",
    mile: `${props.mileageSet.prof_entry?.mile || "50"}`,
    description: "マイル※1",
    icon: "/images/mypage/mile/profile.svg",
  },
  {
    title: "大切な方の誕生日登録",
    mile: `${props.mileageSet.important_bdays?.mile || "50"}`,
    description: "マイル※2",
    icon: "/images/mypage/mile/birthday.svg",
  },
  {
    title: "お気に入り商品登録",
    mile: `${props.mileageSet.fav?.mile || "10"}`,
    description: "マイル※3",
    icon: "/images/mypage/mile/fav.svg",
  },
  {
    title: "商品レビュー投稿",
    mile: `${props.mileageSet.review_post?.mile || "20"}`,
    description: "マイル",
    icon: "/images/mypage/mile/review_post.svg",
  },
  {
    title: "続々追加予定",
    description: "Coming soon",
    icon: "/images/mypage/mile/tential-logo.svg",
  },
]

const firstNoteList = [
  "※ギフト購入でのマイル付与は、2024年6月17日より開始いたします。",
  "※店舗ではお会計時にマイページの会員証をご提示ください。",
  "※オンラインストアでのお買い物は、商品発送の翌日にマイルを反映します。",
  "※返品やキャンセルなどによって一度付与したマイルを取り消す場合がございます。",
]

const secondNoteList = [
  "※1：お1人様1回までの付与となります。",
  "※2：お1人様3人まで大切な方の誕生日を登録でき、最大3回までの付与となります。",
  "※3：お1人様1年間20商品までの付与となります。",
]
</script>

<style scoped module lang="scss">
a {
  @include link-style;
}
.action {
  padding: 0 1rem 44px 1rem;
  border-bottom: 1px solid $primary-20;
  margin-bottom: 44px;

  h4 {
    @include main-font;
    margin-bottom: 18px;
    font-size: $font-size-20;
    color: $primary;
  }

  h5 {
    @include main-font;
    font-size: $font-size-15;
    color: $primary;
    margin-bottom: 16px;
  }

  .table_container {
    overflow-x: scroll;
    margin-bottom: 40px;
    .stage_table {
      table-layout: fixed;
      width: 910px;
    }

    .stage_table,
    .event_table {
      border-spacing: 0;
      border-collapse: separate;
      border: 1px solid $black-20;

      th,
      td {
        padding: 1rem;
        font-size: $font-size-12;
        text-align: center;
        white-space: nowrap;
        border-right: 1px solid $black-20;
        border-bottom: 1px solid $black-20;
      }

      th {
        background-color: #faf5ee;
      }

      thead {
        th {
          font-size: $font-size-12;
          font-weight: normal;
        }
        td {
          text-align: left;
          vertical-align: baseline;

          &.regular {
            background-color: $secondary-20;
          }
          &.silver {
            background-color: #f4f4f4;
          }
          &.gold {
            background-color: #faf5ee;
          }
          &.platinum {
            background-color: #ebebeb;
          }

          h6 {
            font-size: $font-size-19;
            @include main-font;
          }
          .mile {
            @include main-font;
            font-size: $font-size-22;
            font-weight: bold;
            margin: 0 0.25rem 0 0.5rem;
          }
        }
      }

      tbody {
        th {
          font-size: $font-size-12;
          font-weight: normal;
        }

        td {
          &.regular {
            background-color: $secondary-10;
          }
          &.silver {
            background-color: #f6f6f6;
          }
          &.gold {
            background-color: #fdf9f6;
          }
          &.platinum {
            background-color: #f1f1f1;
          }
        }
      }
    }
    .event_table {
      tbody {
        td {
          background-color: #fdf9f6;
          &.event_cell {
            background-color: #faf5ee;
          }
        }
      }
    }
  }
  .note_box {
    padding-bottom: 44px;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    border-bottom: 1px solid $primary-20;
    margin-bottom: 44px;
  }

  .heading_box {
    margin-bottom: 24px;
  }

  .action_box {
    padding-bottom: 44px;
    border-bottom: 1px solid $primary-20;
    margin-bottom: 44px;
  }
}
</style>
